<template>
  <div
    class="ap-card"
    :style="bgStyle"
    @click="onBodyTap"
  >
    <div class="ap-bg-overlay" />

    <!-- Flash icon on tap -->
    <transition name="ap-flash">
      <div v-if="showFlash" class="ap-flash-icon">
        <svg v-if="isPlaying" viewBox="0 0 24 24" fill="white" width="80" height="80"><path d="M8 5v14l11-7z"/></svg>
        <svg v-else           viewBox="0 0 24 24" fill="white" width="80" height="80"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
      </div>
    </transition>

    <!-- Top-left cache button — sibling of ap-action-bar, not inside it -->
    <!-- <button class="ap-cache-btn" @click.stop="toggleCache" :disabled="isSaving"> -->
      <button class="ap-cache-btn" @click.stop="toggleCache" :disabled="isSaving">
      <!-- Saving spinner -->
      <svg v-if="isSaving" class="ap-cache-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
        <path d="M12 2a10 10 0 0 1 10 10" stroke="#00e5ff"/>
      </svg>
      <!-- Not cached -->
      <svg v-else-if="!isCached" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
      <!-- Cached -->
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="#00e5ff" stroke-width="2" width="20" height="20">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
      <span class="ap-cache-label">{{ isSaving ? 'Saving…' : isCached ? 'Saved' : 'Save' }}</span>
    </button>

    <!-- ── Right action bar ── -->
    <div class="ap-action-bar" @click.stop>
      <button class="ap-action-btn ap-avatar-btn" @click="emit('addToPlaylist', infos)">
        <div class="ap-avatar-ring">
          <img src="./img/playlist.svg" class="ap-avatar-img" />
        </div>
        <span class="ap-follow-dot">+</span>
      </button>

      <button class="ap-action-btn" @click="emit('comment', infos)">
        <svg viewBox="0 0 24 24" class="ap-action-icon" fill="currentColor">
          <path d="M21 6.5C21 5.12 19.88 4 18.5 4h-13C4.12 4 3 5.12 3 6.5v8C3 15.88 4.12 17 5.5 17H7l3 4 3-4h5.5c1.38 0 2.5-1.12 2.5-2.5v-8z"/>
        </svg>
        <span class="ap-action-count">{{ formatCount(infos.stats?.comments) }}</span>
      </button>

      <button class="ap-action-btn" :class="{ 'ap-liked': isLiked }" @click="emit('like')">
        <svg viewBox="0 0 24 24" class="ap-action-icon" fill="currentColor">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
        <span class="ap-action-count">{{ formatCount(infos.likes?.length) }}</span>
      </button>

      <button class="ap-action-btn" @click="emit('share')">
        <svg viewBox="0 0 24 24" class="ap-action-icon" fill="currentColor">
          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
        </svg>
        <span class="ap-action-count">{{ formatCount(infos.stats?.shares) }}</span>
      </button>

      <div
        class="ap-disc"
        :class="{ 'ap-disc--spinning': isPlaying }"
        @click.stop="emit('publisher-click', infos.userId)"
      >
        <img
          v-if="userInfos.profilImgMini1"
          :src="store.state.baseUrl + 'images/' + userInfos.profilImgMini1"
          class="ap-disc-img"
        />
        <div v-else class="ap-disc-placeholder">
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
          </svg>
        </div>
      </div>
    </div>

    <!-- ── Viz canvas ── -->
    <div class="ap-viz" ref="vizRef">
      <!-- replace the <canvas> with this, in both files' viz container -->
      <div class="idle-bars" v-show="isPlaying">
        <span></span><span></span><span></span><span></span>
      </div>
    </div>

    <!-- ── Bottom info ── -->
    <div class="ap-bottom" @click.stop>
      <button class="ap-publisher" @click.stop="emit('publisher-click', infos.userId)">
        @{{ userInfos?.pseudo }}
        <span class="ap-badge">{{ infos.type === 'private' ? '🔒' : '🌐' }}</span>
      </button>
      <p v-if="infos.text" class="ap-text">{{ infos.text }}</p>
      <div v-if="infos.tags?.length" class="ap-tags">
        <span v-for="tag in infos.tags" :key="tag" class="ap-tag">#{{ tag }}</span>
      </div>
      <div class="ap-meta">
        <span>👁 {{ formatCount(infos.views?.length) }}</span>
        <span>{{ timeAgo }}</span>
      </div>
    </div>

    <div v-if="loadError" class="ap-error" @click.stop>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="40" height="40">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <p>This audio is no longer available</p>
    </div>
    <!-- ── Controls ── -->
    <div class="ap-controls" @click.stop>
      <div class="seek-btns">
        <button class="seek-btn" @click.stop="seekBackward">
          <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
            <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
          </svg>
          <span class="seek-label">10</span>
        </button>
        <button class="seek-btn" @click.stop="seekForward">
          <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
            <path d="M12.01 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
          </svg>
          <span class="seek-label">10</span>
        </button>
      </div>
      <div
        class="ap-progress-wrap"
        ref="progressRef"
        @mousedown="onProgressMousedown"
        @touchstart.prevent="onProgressTouchstart"
      >
        <div class="ap-progress-bg">
          <div class="ap-progress-fill" ref="progressFillRef" />
          <div class="ap-progress-thumb" ref="progressThumbRef" />
        </div>
        <div class="ap-time-row">
          <span>{{ formatTime(currentTime) }}</span>
          <span>{{ formatTime(infos.duration || 0) }}</span>
        </div>
      </div>

      <div class="ap-ctrl-row">
        <button class="ap-ctrl-btn ap-speed-btn" @click="changeSpeed(-0.1)">
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"/>
          </svg>
        </button>
        <div class="ap-speed-display">{{ currentSpeed.toFixed(1) }}×</div>
        <button class="ap-ctrl-btn ap-speed-btn" @click="changeSpeed(0.1)">
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"/>
          </svg>
        </button>

        <div style="flex:1"></div>

        <button class="ap-ctrl-btn ap-nav-btn" @click="emit('prev')">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/>
          </svg>
        </button>

        <button class="ap-ctrl-btn ap-play-btn" @click="togglePlay">
          <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor" width="26" height="26"><path d="M8 5v14l11-7z"/></svg>
          <svg v-else            viewBox="0 0 24 24" fill="currentColor" width="26" height="26"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
        </button>

        <button class="ap-ctrl-btn ap-nav-btn" @click="emit('next')">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M6 18l8.5-6L6 6v12zm2-8.14L11.03 12 8 14.14V9.86zM16 6h2v12h-2z"/>
          </svg>
        </button>

<!--         <div style="flex:1"></div>

        <button class="ap-ctrl-btn" @click="toggleMute">
          <svg v-if="!isMuted" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
          </svg>
        </button> -->
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
// AudioPlayer script
import { ref, computed, watch, onMounted, onUnmounted, markRaw } from 'vue'
import { isNativeAudio, NativeAudioElement, NativeAudio } from '@/plugin/nativeAudio'
import { useStore } from 'vuex'
import { setupMediaControls, updatePlaybackState, destroyMediaControls } from '@/plugin/mediaControls'

const store = useStore()

const props = defineProps({
  infos:  { type: Object,  default: () => ({}) },
  active: { type: Boolean, default: false },
})

const emit = defineEmits(['publisher-click','comment','like','share','addToPlaylist','played','ended','next','prev'])

const userInfos = computed(() => store.state.users[props.infos.userId] || {})

// ── Template refs ─────────────────────────────────────────────────────────────
const vizRef           = ref(null)
const progressRef      = ref(null)
const progressFillRef  = ref(null)
const progressThumbRef = ref(null)

// ── Reactive state ────────────────────────────────────────────────────────────
const isPlaying    = ref(false)
const isMuted      = ref(false)
const showFlash    = ref(false)
const isLiked      = ref(false)
const isShared     = ref(false)
const currentTime  = ref(0)
const currentSpeed = ref(1.0)
const isCached     = ref(false)
const isSaving     = ref(false)
const loadError    = ref(false)

// ── Private non-reactive vars ─────────────────────────────────────────────────
let _el               = null
let _playVersion      = 0
let _isDragging       = false
let _flashTimer       = null
let _lastTimeUpdate   = 0
let _lastPositionUpdate = 0
let _blobUrl          = null

// ── Computed ──────────────────────────────────────────────────────────────────
const myId = computed(() => props.infos.audio || props.infos._id || 'unknown')

const bgStyle = computed(() => {
  if (!props.infos.img) return { backgroundImage: `url(${require('./img/publication.svg')})` }
  return { backgroundImage: `url(${store.state.baseUrl}images/${props.infos.img})` }
})

const timeAgo = computed(() => {
  if (!props.infos.createdAt) return ''
  const s = (Date.now() - new Date(props.infos.createdAt)) / 1000 | 0
  if (s < 60)    return `${s}s`
  if (s < 3600)  return `${s / 60 | 0}m`
  if (s < 86400) return `${s / 3600 | 0}h`
  return `${s / 86400 | 0}d`
})

// ── Destroy Audio element ─────────────────────────────────────────────────────
function destroyAudio() {
  if (!_el) return
  if (isNativeAudio) { _el.pause(); _el.dispose?.(); _el = null; return }
  _el.pause()
  _el.removeEventListener('timeupdate',     onTimeUpdate)
  _el.removeEventListener('ended',          onEnded)
  _el.removeEventListener('loadedmetadata', onMetadata)
  _el.removeEventListener('error',          onAudioError)
  _el.src = ''
  _el     = null
  if (_blobUrl) {
    URL.revokeObjectURL(_blobUrl)
    _blobUrl = null
  }
}

// ── Progress DOM helpers ──────────────────────────────────────────────────────
function updateProgressDOM(pct) {
  if (progressFillRef.value)  progressFillRef.value.style.width = pct + '%'
  if (progressThumbRef.value) progressThumbRef.value.style.left = pct + '%'
}
function calcPct() {
  const dur = _el?.duration || props.infos.duration || 1
  return Math.min(100, (currentTime.value / dur) * 100)
}

// ── Media controls (web Media Session + Android bridge) ───────────────────────
function syncPlaybackState() {
  const dur = _el?.duration || props.infos.duration || 0
  const pos = _el?.currentTime || currentTime.value || 0
  
  updatePlaybackState({
    isPlaying: isPlaying.value,
    position:  pos,
    duration:  dur,
    speed:     currentSpeed.value || 1,
  })
}

function initMediaControls() {
  setupMediaControls({
    title:  props.infos.text || 'Publication',
    artist: userInfos.value?.pseudo || '',
    cover:  props.infos.imgMini
              ? store.state.baseUrl + 'images/' + props.infos.imgMini
              : '',
    handlers: {
      onPlay:    () => { if (!isPlaying.value) togglePlay() },
      onPause:   () => { if (isPlaying.value)  togglePlay() },
      onStop:    () => stopPlay(),
      onNext:    () => emit('next'),
      onPrev:    () => emit('prev'),
      onSeek:    (t) => {
        if (!_el || !isFinite(t)) return
        const dur     = _el.duration || props.infos.duration || 0
        const clamped = Math.min(Math.max(t, 0), dur)
        _el.currentTime   = clamped
        currentTime.value = clamped
        updateProgressDOM(calcPct())
        syncPlaybackState()
      },
      onForward: () => { seekForward() },
      onRewind:  () => { seekBackward() },
    }
  })
  setTimeout(() => syncPlaybackState(), 300)
}

// ── Audio element event handlers ──────────────────────────────────────────────
function onTimeUpdate() {
  if (_isDragging) return
  const now = Date.now()
  if (now - _lastTimeUpdate < 80) return
  _lastTimeUpdate   = now
  currentTime.value = _el?.currentTime || 0
  updateProgressDOM(calcPct())
  store.commit('player/setCurrentTime', currentTime.value)
  const dur = _el?.duration
  if (dur && !isNaN(dur)) store.commit('player/setDuration', dur)

  if (now - _lastPositionUpdate > 2000) {
    _lastPositionUpdate = now
    syncPlaybackState()
  }
}

function onMetadata() {
  store.commit('player/setDuration', _el?.duration || props.infos.duration || 0)
  setTimeout(() => syncPlaybackState(), 50)
}

function onAudioError() {
  loadError.value   = true
  isPlaying.value   = false
  currentTime.value = 0
  store.commit('setPlayingAudioId', null)
  store.commit('setAudioPlayerIsPlaying', false)
}

function onEnded() {
  isPlaying.value   = false
  currentTime.value = 0
  updateProgressDOM(0)
  destroyMediaControls()
  store.commit('setPlayingAudioId', null)
  store.commit('setAudioPlayerIsPlaying', false)
  store.commit('setCurrentPlayer', null)
  emit('ended')
}

// ── Resolve audio src (cache-first) ──────────────────────────────────────────
async function resolveAudioSrc() {
  const serverUrl = store.state.baseUrl + 'audios/' + props.infos.audio
  if (isNativeAudio) return { src: serverUrl, fromCache: false }
  if (!('caches' in window)) return { src: serverUrl, fromCache: false }
  try {
    const cache  = await caches.open('audio-cache-v1')
    const cached = await cache.match(serverUrl, { ignoreSearch: true, ignoreVary: true })
    if (cached) {
      const blob = await cached.blob()
      _blobUrl   = URL.createObjectURL(blob)
      return { src: _blobUrl, fromCache: true }
    }
  } catch (e) {
    console.warn('Cache read failed:', e)
  }
  return { src: serverUrl, fromCache: false }
}

// ── startPlay — fresh Audio element every time, version-guarded ───────────────
async function startPlay(ghostEl = null) {
  if (!props.infos.audio) return

  const myVersion = ++_playVersion

  destroyAudio()
  isPlaying.value   = false
  currentTime.value = 0
  updateProgressDOM(0)

  let el

  if (isNativeAudio) {
    el = markRaw(new NativeAudioElement(
      myId.value,
      () => ({
        title:   props.infos.text || 'Publication',
        artist:  userInfos.value?.pseudo || '',
        artwork: props.infos.imgMini ? store.state.baseUrl + 'images/' + props.infos.imgMini : '',
      }),
      'audioPlayers'
    ))
    el.src = store.state.baseUrl + 'audios/' + props.infos.audio
    el.playbackRate = currentSpeed.value
    el.addEventListener('timeupdate',     onTimeUpdate)
    el.addEventListener('ended',          onEnded)
    el.addEventListener('loadedmetadata', onMetadata)
  } else if (
    ghostEl &&
    ghostEl.src &&
    ghostEl.src.includes(props.infos.audio) &&
    ghostEl.crossOrigin === 'anonymous'
  ) {
    el = ghostEl
  } else {
    el              = new Audio()
    el.crossOrigin  = 'anonymous'
    el.preload      = 'auto'
  }

  el.muted        = isMuted.value
  el.playbackRate = currentSpeed.value

  const { src, fromCache } = await resolveAudioSrc()
  if (!isNativeAudio && (!el.src || !el.src.includes(props.infos.audio))) {
    if (!fromCache) el.crossOrigin = 'anonymous'
    el.src = src
  }

  _el = el

  if (!isNativeAudio) {
    el.addEventListener('timeupdate',     onTimeUpdate)
    el.addEventListener('ended',          onEnded)
    el.addEventListener('loadedmetadata', onMetadata)
    el.addEventListener('error',          onAudioError)

    await new Promise(resolve => {
      if (el.readyState >= 2) { resolve(); return }
      el.addEventListener('canplay', resolve, { once: true })
      el.addEventListener('error',   resolve, { once: true })
    })
  }

  if (myVersion !== _playVersion || _el !== el) {
    if (el.src && !isNativeAudio) el.src = ''
    return
  }

  store.commit('setPlayingAudioId', myId.value)
  try {
    await el.play()
    if (myVersion !== _playVersion || _el !== el) {
      if (!isNativeAudio) { el.pause(); el.src = '' }
      else el.pause()
      return
    }
    isPlaying.value = true
    initMediaControls()
    store.commit('setCurrentPlayer', 'audioPlayer')
    emit('played')
    store.commit('setAudioPlayerIsPlaying', true)
    store.commit('player/setDuration', el.duration || props.infos.duration || 0)
    store.commit('player/setMeta', {
      userName:   userInfos.value?.pseudo || '',
      userAvatar: userInfos.value?.profilImgMini1 || '',
      text:       props.infos.text || '',
      img:        props.infos.img  || '',
      imgMini:    props.infos.imgMini || '',
    })
  } catch (err) {
    if (err.name === 'AbortError') return
    if (!isNativeAudio && (err.name === 'NotSupportedError' || el.error)) {
      loadError.value = true
      return
    }
    console.warn('startPlay failed:', err)
    store.commit('setPlayingAudioId', null)
    store.commit('setAudioPlayerIsPlaying', false)
  }
}

// ── stopPlay ──────────────────────────────────────────────────────────────────
function stopPlay() {
  _playVersion++
  destroyAudio()
  isPlaying.value   = false
  currentTime.value = 0
  updateProgressDOM(0)
  destroyMediaControls()
  store.commit('setPlayingAudioId', null)
  store.commit('setAudioPlayerIsPlaying', false)
  store.commit('setCurrentPlayer', null)
}

// ── togglePlay ────────────────────────────────────────────────────────────────
async function togglePlay() {
  if (isPlaying.value) {
    if (_el) _el.pause()
    isPlaying.value = false
    store.commit('setPlayingAudioId', null)
    store.commit('setAudioPlayerIsPlaying', false)
    store.commit('setCurrentPlayer', null)
    syncPlaybackState()
  } else if (_el) {
    store.commit('setPlayingAudioId', myId.value)
    try {
      await _el.play()
      isPlaying.value = true
      initMediaControls()
      store.commit('setAudioPlayerIsPlaying', true)
      store.commit('setCurrentPlayer', 'audioPlayer')
      syncPlaybackState()
    } catch (err) {
      if (err.name === 'AbortError') return
      console.warn('resume failed:', err)
      store.commit('setPlayingAudioId', null)
      store.commit('setAudioPlayerIsPlaying', false)
    }
  } else {
    await startPlay()
  }
}

// ── Speed ─────────────────────────────────────────────────────────────────────
function changeSpeed(delta) {
  let s = Math.round((currentSpeed.value + delta) * 10) / 10
  if (s < 0.5) s = 0.5
  if (s > 2.0) s = 2.0
  currentSpeed.value = s
  if (_el) _el.playbackRate = s
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function getRatio(clientX) {
  const rect = progressRef.value.getBoundingClientRect()
  let r = (clientX - rect.left) / rect.width
  if (r < 0) r = 0; if (r > 1) r = 1
  return r
}

function applySeek(ratio) {
  const dur = _el?.duration || props.infos.duration || 0
  if (!dur) return
  currentTime.value = ratio * dur
  if (_el) _el.currentTime = ratio * dur
  updateProgressDOM(ratio * 100)
  syncPlaybackState()
}

function onProgressMousedown(e)  { _isDragging = true;  applySeek(getRatio(e.clientX)) }
function onProgressMousemove(e)  { if (_isDragging) applySeek(getRatio(e.clientX)) }
function onProgressMouseup(e)    { if (!_isDragging) return; _isDragging = false; applySeek(getRatio(e.clientX)) }
function onProgressTouchstart(e) { _isDragging = true;  applySeek(getRatio(e.touches[0].clientX)) }
function onProgressTouchmove(e)  { if (!_isDragging) return; e.preventDefault(); applySeek(getRatio(e.touches[0].clientX)) }
function onProgressTouchend(e)   { if (!_isDragging) return; _isDragging = false; applySeek(getRatio(e.changedTouches[0].clientX)) }

// ── Body tap ──────────────────────────────────────────────────────────────────
function onBodyTap() {
  togglePlay()
  showFlash.value = true
  clearTimeout(_flashTimer)
  _flashTimer = setTimeout(() => { showFlash.value = false }, 600)
}

// ── Seek ±10s ─────────────────────────────────────────────────────────────────
function seekBackward() {
  const dur = _el?.duration || props.infos.duration || 0
  if (!dur) return
  const t = Math.max(0, (_el?.currentTime || currentTime.value) - 10)
  if (_el) _el.currentTime = t
  currentTime.value = t
  updateProgressDOM(calcPct())
  syncPlaybackState()
}

function seekForward() {
  const dur = _el?.duration || props.infos.duration || 0
  if (!dur) return
  const t = Math.min(dur, (_el?.currentTime || currentTime.value) + 10)
  if (_el) _el.currentTime = t
  currentTime.value = t
  updateProgressDOM(calcPct())
  syncPlaybackState()
}

// ── Cache helpers ─────────────────────────────────────────────────────────────
async function checkCached() {
  if (!props.infos.audio) return
  const url = store.state.baseUrl + 'audios/' + props.infos.audio
  if (isNativeAudio) {
    isCached.value = await NativeAudio.isCached(url)
    return
  }
  if (!('caches' in window)) return
  const cache = await caches.open('audio-cache-v1')
  isCached.value = !!(await cache.match(url))
}

async function toggleCache() {
  if (!props.infos.audio || isSaving.value) return
  const url = store.state.baseUrl + 'audios/' + props.infos.audio
  if (isNativeAudio) {
    isSaving.value = true
    try {
      if (isCached.value) {
        const success = await NativeAudio.unsaveTrack(url)
        if (success) isCached.value = false
      } else {
        const success = await NativeAudio.cacheTrack(url)
        if (success) isCached.value = true
      }
    } catch (e) {
      console.warn('Native cache/unsave failed:', e)
    } finally {
      isSaving.value = false
    }
    return
  }
  if (!('caches' in window)) return
  const cache = await caches.open('audio-cache-v1')

  if (isCached.value) {
    await cache.delete(url)
    isCached.value = false
  } else {
    isSaving.value = true
    try {
      const response = await fetch(url, { cache: 'no-store' })
      if (response.status === 200) {
        await cache.put(url, response)
        isCached.value = true
      } else if (response.status === 206) {
        const blob         = await response.blob()
        const fullResponse = new Response(blob, {
          status:  200,
          headers: { 'Content-Type': response.headers.get('Content-Type') || 'audio/mpeg' }
        })
        await cache.put(url, fullResponse)
        isCached.value = true
      } else {
        console.warn('Unexpected status:', response.status)
      }
    } catch (e) {
      console.warn('Cache failed:', e)
    } finally {
      isSaving.value = false
    }
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatCount(n = 0) {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M'
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K'
  return String(n)
}
function formatTime(s = 0) {
  const m = s / 60 | 0, sec = s % 60 | 0
  return `${m}:${sec < 10 ? '0' + sec : sec}`
}

function initLiked() {
  const likes = props.infos.likes || []
  isLiked.value = !!likes.find(lk => lk.userId === store.state.user.uid)
}

// ── Watchers ──────────────────────────────────────────────────────────────────
watch(() => props.infos, () => {
  initLiked()
  checkCached()
  loadError.value = false
}, { immediate: true })

watch(() => store.state.currentPlayer, (val) => {
  if (val !== 'audioPlayer' && isPlaying.value) {
    if (_el) _el.pause()
    isPlaying.value = false
    store.commit('setAudioPlayerIsPlaying', false)
    syncPlaybackState()
  }
})

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(() => {
  window.addEventListener('mouseup',   onProgressMouseup)
  window.addEventListener('mousemove', onProgressMousemove)
  window.addEventListener('touchend',  onProgressTouchend)
  window.addEventListener('touchmove', onProgressTouchmove, { passive: false })
})

onUnmounted(() => {
  stopPlay()
  window.removeEventListener('mouseup',   onProgressMouseup)
  window.removeEventListener('mousemove', onProgressMousemove)
  window.removeEventListener('touchend',  onProgressTouchend)
  window.removeEventListener('touchmove', onProgressTouchmove)
  destroyMediaControls()
})

defineExpose({ isLiked, isShared, startPlay, stopPlay, togglePlay })
</script>

<style scoped>
.ap-card {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: #0d1130;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  user-select: none;
}

.ap-bg-overlay {
  position: absolute;
  bottom: 0px;
  height: 100%;
  width:100%;
  z-index: 1;
  background: linear-gradient(
    to bottom,
    rgba(10,14,38,0.55) 0%,
    rgba(10,14,38,0.20) 30%,
    rgba(10,14,38,0.75) 65%,
    rgba(10,14,38,0.98) 100%
  );
}

.ap-flash-icon {
  position: absolute;
  width: 100%; height: 100%;
  bottom:110px;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.ap-flash-enter-active { transition: opacity 0.05s, transform 0.05s; }
.ap-flash-leave-active { transition: opacity 0.5s ease-out, transform 0.5s ease-out; }
.ap-flash-enter-from   { opacity: 0; transform: scale(0.6); }
.ap-flash-leave-to     { opacity: 0; transform: scale(1.4); }

.ap-action-bar {
  position: absolute;
  right: 12px;
  bottom: 0;
  z-index: 10;
  margin-bottom: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.ap-action-bar > * + * { margin-top: 22px; }

.ap-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  color: rgba(255,255,255,0.9);
  cursor: pointer;
  padding: 0;
}
.ap-action-btn > * + * { margin-top: 4px; }
.ap-action-btn:active  { transform: scale(0.88); }
.ap-liked .ap-action-icon { color: #ff4d6d; }
.ap-action-icon  { width: 32px; height: 32px; }
.ap-action-count { font-size: 14px; font-weight: 600; }

.ap-avatar-btn { position: relative; }
.ap-avatar-ring {
  width: 54px; height: 54px;
  border-radius: 50%;
  border: 2px solid #fff;
  overflow: hidden;
  background: #1a2150;
  display: flex; align-items: center; justify-content: center;
}
.ap-avatar-img { width: 100%; height: 100%; object-fit: cover; }
.ap-follow-dot {
  position: absolute;
  bottom: -4px; left: 50%;
  transform: translateX(-50%);
  width: 24px; height: 24px;
  border-radius: 50%;
  background: #ff4d6d;
  color: #fff;
  font-size: 18px; font-weight: 700;
  border: 2px solid #0a0e26;
  display: flex; align-items: center; justify-content: center;
}

.ap-disc {
  width: 50px; height: 50px;
  border-radius: 50%;
  border: 3px solid rgba(255,255,255,0.3);
  overflow: hidden;
  background: #1a2150;
  display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,0.7);
  cursor: pointer;
}
.ap-disc-img { width: 100%; height: 100%; object-fit: cover; }
.ap-disc-placeholder svg { width: 22px; height: 22px; }
.ap-disc--spinning { animation: ap-spin 3s linear infinite; }
@keyframes ap-spin { to { transform: rotate(360deg); } }

.ap-viz {
  position: relative;
  z-index: 3;
  width: 100%;
  height: 70px;
  margin-bottom: 8px;
}
.ap-canvas { width: 100%; height: 100%; display: block; }

.ap-bottom {
  position: relative;
  z-index: 4;
  padding: 0 16px 10px;
  max-width: calc(100% - 78px);
}
.ap-publisher {
  background: none; border: none;
  cursor: pointer; color: #fff;
  font-size: 17px; font-weight: 700;
  padding: 0; margin: 0 0 4px;
  display: flex; align-items: center;
  font-family: inherit;
}
.ap-publisher > * + * { margin-left: 6px; }
.ap-badge { font-size: 15px; }
.ap-text  {
  color: rgba(255,255,255,0.82);
  font-size: 15px; line-height: 1.45;
  margin: 0 0 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ap-tags { display: flex; flex-wrap: wrap; margin-bottom: 4px; }
.ap-tags > * + * { margin-left: 5px; }
.ap-tag  { font-size: 14px; color: #8ecdf7; font-weight: 600; }
.ap-meta { display: flex; font-size: 13px; color: rgba(255,255,255,0.4); }
.ap-meta > * + * { margin-left: 14px; }


.ap-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px;
  background: rgba(10, 14, 38, 0.85);
  border: 1px solid rgba(255, 59, 48, 0.3);
  border-radius: 16px;
  color: rgba(255, 255, 255, 0.7);
  pointer-events: none;
}
.ap-error > * + * { margin-top: 12px; }
.ap-error svg     { color: #ff3b30; }
.ap-error p       { font-size: 14px; margin: 0; }

.ap-controls {
  position: relative;
  z-index: 5;
  padding: 12px 16px 10px;
  background: rgba(8,12,35,0.85);
  background: linear-gradient(to top, rgba(8,12,35,0.85), rgba(8,12,35,0.05));
}

.ap-progress-wrap { padding: 0 0 4px; cursor: grab; user-select: none; }
.ap-progress-wrap:active { cursor: grabbing; }

.ap-progress-bg {
  position: relative;
  height: 5px;
  background: rgba(255,255,255,0.12);
  border-radius: 99px;
  overflow: visible;
}
.ap-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e5ff, #a29bfe);
  border-radius: 99px;
  width: 0%;
  will-change: width;
}
.ap-progress-thumb {
  position: absolute;
  top: 50%; left: 0%;
  transform: translate(-50%, -50%);
  width: 14px; height: 14px;
  border-radius: 50%;
  background: #fff;
  pointer-events: none;
  will-change: left;
}
.ap-time-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: rgba(255,255,255,0.4);
  margin-top: 5px;
  font-family: 'Space Mono', monospace;
}

.ap-ctrl-row {
  display: flex;
  align-items: center;
  margin-top: 10px;
}
.ap-ctrl-row > * + * { margin-left: 10px; }

.ap-ctrl-btn {
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: rgba(255,255,255,0.85);
  padding: 9px 12px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.ap-ctrl-btn:hover  { background: rgba(255,255,255,0.14); }
.ap-ctrl-btn:active { transform: scale(0.92); }
.ap-ctrl-btn svg { width: 20px; height: 20px; }

.ap-speed-btn { padding: 9px 11px; }
.ap-speed-btn svg { width: 18px; height: 18px; }
.ap-speed-display {
  font-size: 15px; font-weight: 700;
  color: #00e5ff;
  min-width: 40px; text-align: center;
  font-family: 'Space Mono', monospace;
}

.ap-nav-btn {
  background: rgba(255,255,255,0.05);
  border-color: rgba(255,255,255,0.08);
  padding: 10px 14px;
}
.ap-nav-btn:hover { background: rgba(255,255,255,0.12); }
.ap-nav-btn svg { width: 23px; height: 23px; }

.ap-play-btn {
  background: linear-gradient(135deg, #00e5ff22, #a29bfe22);
  border-color: rgba(0,229,255,0.35);
  padding: 12px 18px;
  color: #fff;
  border-radius: 50%;
  width: 60px; height: 60px;
}
.ap-play-btn:hover { background: linear-gradient(135deg, #00e5ff44, #a29bfe44); }
.ap-play-btn svg { width: 30px; height: 30px; }


.seek-btns {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  padding-top: 0px;
  opacity: 1;
  transition: opacity 0.2s ease;
  z-index: 9;
}
.seek-btns > * + * { margin-left: 48px; }

/* show when controls are visible */
.controls-overlay .seek-btns,
.ap-controls-visible .seek-btns {
  pointer-events: auto;
  opacity: 1;
}

.seek-btn {
  position: relative;
  background: rgba(0, 0, 0, 0.35);
  border: 1.5px solid rgba(255,255,255,0.2);
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255,255,255,0.9);
}
.seek-btn:active { transform: scale(0.9); }
.seek-btn svg { width: 32px; height: 32px; }

.seek-label {
  position: absolute;
  bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(255,255,255,0.8);
  pointer-events: none;
}


.ap-cache-btn {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 10;
  display: flex;
  align-items: center;
  background: rgba(10, 14, 38, 0.7);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 20px;
  padding: 6px 12px;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  transition: background 0.2s;
}
.ap-cache-btn > * + * { margin-left: 6px; }
.ap-cache-btn:hover:not(:disabled) { background: rgba(10,14,38,0.9); }
.ap-cache-btn:disabled { opacity: 0.6; cursor: default; }

.ap-cache-label {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.ap-cache-spinner {
  width: 20px;
  height: 20px;
  animation: ap-spin 0.8s linear infinite;
  flex-shrink: 0;
}
.idle-bars {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 100%;
  justify-content: center;
}
.idle-bars span {
  width: 4px;
  border-radius: 2px;
  background: linear-gradient(180deg, #00e5ff, #a29bfe);
  animation: idle-bar-bounce 1.1s ease-in-out infinite;
}
.idle-bars span:nth-child(1) { animation-delay: 0s;    }
.idle-bars span:nth-child(2) { animation-delay: 0.15s; }
.idle-bars span:nth-child(3) { animation-delay: 0.3s;  }
.idle-bars span:nth-child(4) { animation-delay: 0.45s; }

@keyframes idle-bar-bounce {
  0%, 100% { height: 20%; }
  50%      { height: 90%; }
}
/* ap-spin already defined in your file — reuse it */
/* ════════════════════════════════════════════════════
   Small-screen override — restore original proportions
   ════════════════════════════════════════════════════ */
@media (max-width: 340px) {
  .ap-action-bar { margin-bottom: 120px; }
  .ap-action-bar > * + * { margin-top: 18px; }
  .ap-action-icon  { width: 28px; height: 28px; }
  .ap-action-count { font-size: 12px; }

  .ap-avatar-ring { width: 46px; height: 46px; }
  .ap-follow-dot {
    width: 20px; height: 20px; font-size: 16px;
  }

  .ap-disc { width: 42px; height: 42px; }
  .ap-disc-placeholder svg { width: 18px; height: 18px; }

  .ap-viz { height: 60px; margin-bottom: 8px; }

  .ap-bottom { max-width: calc(100% - 70px); }

  .ap-publisher { font-size: 15px; }
  .ap-badge { font-size: 13px; }
  .ap-text  { font-size: 13px; }
  .ap-tag  { font-size: 12px; }
  .ap-meta { font-size: 11px; }
  .ap-meta > * + * { margin-left: 12px; }

  .ap-controls { padding: 10px 16px 8px; }

  .ap-progress-bg { height: 4px; }
  .ap-progress-thumb { width: 12px; height: 12px; }
  .ap-time-row { font-size: 10px; }

  .ap-ctrl-row { margin-top: 10px; }
  .ap-ctrl-row > * + * { margin-left: 8px; }

  .ap-ctrl-btn { padding: 7px 10px; }
  .ap-ctrl-btn svg { width: 18px; height: 18px; }

  .ap-speed-btn { padding: 7px 9px; }
  .ap-speed-btn svg { width: 16px; height: 16px; }
  .ap-speed-display { font-size: 13px; min-width: 36px; }

  .ap-nav-btn { padding: 8px 12px; }
  .ap-nav-btn svg { width: 20px; height: 20px; }

  .ap-play-btn { padding: 10px 16px; width: 52px; height: 52px; }
  .ap-play-btn svg { width: 26px; height: 26px; }

  .seek-btns > * + * { margin-left: 40px; }
  .seek-btn { width: 56px; height: 56px; }
  .seek-btn svg { width: 28px; height: 28px; }
  .seek-label { font-size: 10px; }
}
</style>